import type {
  ReportCatalog,
  ReportQueryResult,
  ReportRecordColumn,
  ReportRecordRow
} from "@gettactic/api";
import * as XLSX from "xlsx";
import { type PairedResult, rowsByKey, seriesByBucket } from "./pair";

export type ExportCell = string | number;

/** The note a records sheet ends on when the selection goes on past the export cap. */
export const truncatedNote = (max: number): string =>
  `Export stopped at ${max.toLocaleString("en-US")} rows; narrow the period or filters to export the rest.`;

/**
 * Rows of a dataset as a sheet: the column labels, then one row per record
 * with numbers kept as numbers and empty cells left empty.
 */
export const recordsToRows = (
  columns: ReportRecordColumn[],
  rows: ReportRecordRow[],
  truncatedAt?: number
): ExportCell[][] => {
  const sheet: ExportCell[][] = [
    columns.map((c) => c.label),
    ...rows.map((row) => row.map((cell) => cell ?? ""))
  ];
  if (truncatedAt !== undefined) {
    sheet.push([truncatedNote(truncatedAt)]);
  }
  return sheet;
};

/**
 * A result as a spreadsheet: one row per breakdown cell, or one per time
 * bucket when there is no breakdown. Ratios are written as percent points
 * so the numbers read the same as on screen. A paired measure is one more
 * column, read beside each row.
 */
export const resultToRows = (
  catalog: ReportCatalog,
  result: ReportQueryResult,
  paired?: PairedResult
): ExportCell[][] => {
  const measure = result.meta.measure;
  const unit =
    measure.kind === "percent" ? " (%)" : measure.unit === "h" ? " (h)" : "";
  const value = (v: number): number =>
    measure.kind === "percent" ? Math.round(v * 1000) / 10 : v;
  // A count with no matching row is a zero; anything else is unknown, and
  // an empty cell says so where a table shows a dash.
  const pairedCell = (v: number | undefined): ExportCell =>
    v !== undefined ? value(v) : paired?.measure.kind === "count" ? 0 : "";
  // A pair is its own comparison: the charts and the table drop the
  // previous period beside one, and so does the export.
  const hasPrevious = result.totals.previous !== null && !paired;
  const valueHeaders = [
    `${measure.label}${unit}`,
    ...(paired ? [`${paired.measure.label}${unit}`] : []),
    ...(hasPrevious ? [`Previous${unit}`] : [])
  ];

  const dimensions = result.columns.filter((c) => c.type === "dimension");
  if (dimensions.length > 0) {
    const pairedRows = paired ? rowsByKey(paired.result.rows) : null;
    const header = [
      ...dimensions.map(
        (c) => catalog.dimensions.find((d) => d.key === c.key)?.label ?? c.key
      ),
      ...valueHeaders
    ];
    const rows = result.rows.map((r) => [
      ...r.labels,
      value(r.value),
      ...(pairedRows
        ? [pairedCell(pairedRows.get(r.keys.join("|"))?.value)]
        : []),
      ...(hasPrevious ? [value(r.previous ?? 0)] : [])
    ]);
    return [header, ...rows];
  }

  const pairedPoints = paired ? seriesByBucket(paired.result.series) : null;
  const header = ["Period", ...valueHeaders];
  const rows = result.series.map((p) => [
    p.bucket,
    value(p.value),
    ...(pairedPoints ? [pairedCell(pairedPoints.get(p.bucket)?.value)] : []),
    ...(hasPrevious ? [value(p.previous ?? 0)] : [])
  ]);
  return [header, ...rows];
};

const csvCell = (cell: ExportCell): string => {
  const text = String(cell);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const rowsToCsv = (rows: ExportCell[][]): string =>
  rows.map((r) => r.map(csvCell).join(",")).join("\n");

/** A file name safe for every OS, from a tile title or board name. */
export const fileSafeName = (name: string): string =>
  name
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80) || "report";

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadCsv = (name: string, rows: ExportCell[][]): void => {
  downloadBlob(
    new Blob([rowsToCsv(rows)], { type: "text/csv;charset=utf-8" }),
    `${fileSafeName(name)}.csv`
  );
};

export type ExportSheet = {
  name: string;
  rows: ExportCell[][];
};

/** Excel sheet names: at most 31 characters, none of []:*?/\, unique. */
export const sheetName = (name: string, taken: Set<string>): string => {
  const base =
    name
      .replace(/[[\]:*?/\\]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 31) || "Sheet";
  let candidate = base;
  for (let i = 2; taken.has(candidate); i++) {
    const suffix = ` (${i})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
  }
  taken.add(candidate);
  return candidate;
};

/** One workbook, one sheet per tile. */
export const downloadXlsx = (name: string, sheets: ExportSheet[]): void => {
  const workbook = XLSX.utils.book_new();
  const taken = new Set<string>();
  for (const sheet of sheets) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(sheet.rows),
      sheetName(sheet.name, taken)
    );
  }
  XLSX.writeFile(workbook, `${fileSafeName(name)}.xlsx`);
};
